import { useCallback, useMemo, useRef, useState } from "react";

import { offset } from "@floating-ui/dom";
import {
  faArchive,
  faChevronDown,
  faCopy,
  faEdit,
  faExclamationCircle,
  faFlask,
  faInfoCircle,
  faLock,
  faRefresh,
  faSearch,
  faSuitcase,
  faTable,
  faTableLayout,
  faUser,
  faWavePulse,
} from "@fortawesome/pro-regular-svg-icons";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { isString, times } from "lodash-es";
import { Navigate, useNavigate } from "react-router-dom";

import { Button } from "src/base-components/Button";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import { OptionsDropdown } from "src/base-components/OptionsDropdown/OptionsDropdown";
import {
  MENU_DIVIDER,
  OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import {
  type BulkActionErrorCaseData,
  BulkActionErrorModal,
  BulkActionErrorType,
} from "src/cases/BulkActionErrorModal";
import { BulkCaseActionModal } from "src/cases/BulkCaseActionWindow";
import { CaseActionFormValues } from "src/cases/caseView/schema";
import { useRefreshQueue } from "src/cases/hooks";
import {
  BulkActionCaseFailure,
  useBulkCaseUpdate,
  useRunBulkCaseActions,
} from "src/cases/queries/cases";
import { useCaseQueues } from "src/cases/queries/queues";
import {
  type ResolvedTemplateVersionStatus,
  useResolvedTemplateVersion,
} from "src/cases/queries/useResolvedTemplateVersion";
import { CaseQueueSummary } from "src/cases/queues/CaseQueueSummary";
import { CasesBulkAssignModal } from "src/cases/queues/CasesBulkAssignModal";
import { CasesFilterPopover } from "src/cases/queues/CasesFilterPopover";
import { CasesTable } from "src/cases/queues/CasesTable";
import {
  CasesTableProvider,
  useCasesTableContext,
} from "src/cases/queues/CasesTableContext";
import { getCaseQueueIcon } from "src/cases/queues/utils";
import { useBulkCaseActions as useBulkCaseActionsState } from "src/cases/useBulkCaseActions";
import { onArchiveCasesError, onAssignCasesError } from "src/cases/utils";
import { CaseTemplateActionOutput } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useCapabilities } from "src/hooks/useCapabilities";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useEnvironment } from "src/hooks/useEnvironment";
import { WhitePane } from "src/jobs/common/WhitePane";
import { PageHeader } from "src/layout/PageHeader";
import {
  SubHeader,
  SubHeaderButton,
  SubHeaderTab,
  SubHeaderTabs,
} from "src/layout/SubHeader";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import {
  CasesPageParams,
  getUrlToCaseQueuePage,
  getUrlToCaseTemplatesPage,
  getUrlToCreateCasePage,
  getUrlToCreateCaseQueuePage,
  getUrlToEditCaseQueuePage,
} from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { copyTextToClipboard } from "src/utils/clipboard";
import { ErrorBoundary } from "src/utils/ErrorBoundary";
import { logger } from "src/utils/logger";
import { pluralize, pluralizeWord } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

const getCreateCaseTooltip = (
  status: ResolvedTemplateVersionStatus,
): string => {
  switch (status) {
    case "ok":
      return "Create a Case";
    case "no_default_version":
      return "This queue requires a selected default template version for creating Cases";
    case "no_published_version":
      return "This queue requires at least one published Case template before Cases can be created";
    case "archived":
      return "The default Case template version used for manual Case creation has been archived. Please select another template on queue configuration.";
  }
};

const INCOMPLETE_TASKS_ERROR = "BulkActionRequiredTasksNotCompletedError";
const CONFLICT_ERROR = "BulkActionCasesHaveQueuedActionsError";

const showBulkActionResultToast = (
  toastId: string,
  action: CaseTemplateActionOutput,
  result: { successfulCaseIds: string[]; failures: BulkActionCaseFailure[] },
  options: {
    onViewErrorCases: (
      cases: BulkActionErrorCaseData[],
      errorType: BulkActionErrorType,
    ) => void;
    onRetry: () => void;
    selectedCases: Array<{
      id: string;
      case_properties?: { status?: string; sub_status?: string };
      template?: { id: string };
      template_version?: { id: string };
    }>;
  },
) => {
  const { successfulCaseIds, failures } = result;
  const { onViewErrorCases, onRetry, selectedCases } = options;

  // All succeeded
  if (failures.length === 0) {
    toastActions.success({
      id: toastId,
      title: `${action.display_name} action completed successfully`,
      description: `Applied to all ${successfulCaseIds.length} ${pluralizeWord(successfulCaseIds.length, "case")}.`,
    });
    return;
  }

  // Partial success
  if (successfulCaseIds.length > 0) {
    toastActions.warning({
      id: toastId,
      title: "Bulk action partially completed",
      description: `Applied to ${successfulCaseIds.length} ${pluralizeWord(successfulCaseIds.length, "case")}. Failed for ${failures.length} ${pluralizeWord(failures.length, "case")}.`,
    });
    return;
  }

  // All failed - check reason
  const casesWithIncompleteTasks = failures
    .filter((f) => f.error === INCOMPLETE_TASKS_ERROR)
    .map((f) => f.caseId);

  const casesWithConflict = failures
    .filter((f) => f.error === CONFLICT_ERROR)
    .map((f) => f.caseId);

  const prepareCasesForModal = (
    caseIds: string[],
  ): BulkActionErrorCaseData[] => {
    return caseIds
      .map((caseId) => {
        const caseData = selectedCases.find((c) => c.id === caseId);
        if (!caseData) return null;
        return {
          id: caseId,
          status: caseData.case_properties?.status || "",
          sub_status: caseData.case_properties?.sub_status,
          templateId: caseData.template?.id || "",
          versionId: caseData.template_version?.id || "",
        };
      })
      .filter(Boolean) as BulkActionErrorCaseData[];
  };

  if (casesWithIncompleteTasks.length > 0) {
    const errorCases = prepareCasesForModal(casesWithIncompleteTasks);
    toastActions.failure({
      id: toastId,
      title: "Bulk action failed",
      description:
        "One or more selected cases has a required task that isn't completed.",
      actionText: "View cases",
      onActionClick: () =>
        onViewErrorCases(errorCases, BulkActionErrorType.INCOMPLETE_TASKS),
    });
    return;
  }

  if (casesWithConflict.length > 0) {
    const errorCases = prepareCasesForModal(casesWithConflict);
    toastActions.failure({
      id: toastId,
      title: "Bulk action failed",
      description:
        "One or more selected cases has another action already in progress.",
      actionText: "View cases",
      onActionClick: () =>
        onViewErrorCases(errorCases, BulkActionErrorType.CONFLICT),
    });
    return;
  }

  // Generic failure
  toastActions.failure({
    id: toastId,
    title: "Bulk action failed",
    description: "Something went wrong. Please try again.",
    actionText: "Retry",
    onActionClick: onRetry,
  });
};

const SubHeaderLoadingTabs: React.FC = () => (
  <div className="flex h-full items-center gap-2" data-loc="sub-header-loading">
    {times(7, (index) => (
      <SkeletonPlaceholder key={index} height="h-2/3" width="w-20" />
    ))}
  </div>
);

const CasesEllipsisMenu: React.FC = () => {
  const navigate = useNavigate();
  const { caseTemplates } = useCapabilities();
  const [environment, setEnvironment] = useEnvironment();
  const { orgId, wsId } = useParamsDecode<CasesPageParams>();

  return (
    <div className="h-full border-gray-200 border-l p-3.5">
      <EllipsisOptionsDropdown
        dataLoc="cases-page-menu"
        elements={
          [
            ...(caseTemplates.canAccess
              ? [
                  {
                    key: "Configure Case Templates",
                    icon: faTableLayout,
                    action: () =>
                      navigate(getUrlToCaseTemplatesPage(orgId, wsId)),
                  },
                  MENU_DIVIDER,
                ]
              : []),
            {
              key: `Switch to ${environment === "sandbox" ? "Live" : "Sandbox"} environment`,
              icon: environment === "sandbox" ? faWavePulse : faFlask,
              action: () =>
                setEnvironment(environment === "sandbox" ? "live" : "sandbox"),
            },
          ].filter(Boolean) as OptionsDropdownElement[]
        }
        iconSize="sm"
        placement="bottom start"
      />
    </div>
  );
};

const SubHeaderRowSelection: React.FC<{
  wsId: string;
  onShowErrorCases: (
    cases: BulkActionErrorCaseData[],
    errorType: BulkActionErrorType,
  ) => void;
}> = ({ wsId, onShowErrorCases }) => {
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [bulkActionModalState, setBulkActionModalState] = useState<{
    isOpen: boolean;
    action: CaseTemplateActionOutput | null;
  }>({ isOpen: false, action: null });
  const formWindowTargetRef = useRef<HTMLDivElement>(null);
  const { open } = useFloatingWindowsActions();
  const { queue_id } = useParamsDecode<CasesPageParams>();
  const [environment] = useEnvironment();
  const { cases } = useCapabilities();

  const { selectedRows, onSelectRow } = useCasesTableContext();
  const currentUserId = useCurrentUserId();
  const { mutateAsync: updateCases, isPending: isAssigning } =
    useBulkCaseUpdate(queue_id);

  const selectedCaseIds = useMemo(
    () =>
      Object.entries(selectedRows)
        .map(([id, selected]) => selected && id)
        .filter(isString),
    [selectedRows],
  );

  const bulkActionsState = useBulkCaseActionsState({
    selectedCaseIds,
    queueId: queue_id,
    environment,
  });

  const { mutateAsync: submitBulkAction } = useRunBulkCaseActions({
    queueId: queue_id,
    wsId,
  });
  const handleAssignCases = useCallback(
    async (assigneeId: Nullable<string>) => {
      if (!selectedCaseIds.length) return;

      const toastId = toastActions.loading({
        title: "Assigning cases...",
        description:
          "Bulk-assigning the selected cases. This may take a few seconds.",
        duration: Infinity,
      });

      try {
        await updateCases({
          queueId: queue_id,
          operation: "assign",
          assigneeId,
          caseIds: selectedCaseIds,
        });

        toastActions.success({
          id: toastId,
          title: "Assigned successfully",
        });
      } catch (error) {
        logger.error("Error assigning cases", error);
        onAssignCasesError(error, {
          id: toastId,
          action: "assigning",
        });
      } finally {
        setIsBulkAssignModalOpen(false);
      }
    },
    [selectedCaseIds, updateCases, queue_id],
  );

  const handleClaimCases = async () => {
    if (!currentUserId || !selectedCaseIds.length) return;

    const toastId = toastActions.loading({
      title: "Claiming cases...",
      description: "Claiming the selected cases. This may take a few seconds",
      duration: Infinity,
    });

    try {
      await updateCases({
        queueId: queue_id,
        operation: "assign",
        assigneeId: currentUserId,
        caseIds: selectedCaseIds,
      });

      toastActions.success({
        id: toastId,
        title: "Claimed successfully",
      });
    } catch (error) {
      logger.error(`Error claiming cases`, error);
      onAssignCasesError(error, {
        id: toastId,
        action: "claiming",
      });
    }
  };

  const handleArchiveCases = async () => {
    if (!selectedCaseIds.length) return;

    const toastId = toastActions.loading({
      title: "Archiving cases...",
      description:
        "Bulk-archiving the selected cases. This may take a few seconds.",
      duration: Infinity,
    });

    try {
      await updateCases({
        queueId: queue_id,
        operation: "archive",
        caseIds: selectedCaseIds,
      });

      toastActions.success({
        id: toastId,
        title: "Cases archived",
      });
    } catch (error) {
      logger.error("Error archiving cases", error);
      onArchiveCasesError(error, {
        id: toastId,
      });
    } finally {
      onSelectRow({});
    }
  };

  const handleBulkActionSubmit = useCallback(
    async (
      action: CaseTemplateActionOutput,
      formData?: CaseActionFormValues,
    ) => {
      if (!selectedCaseIds.length) return;

      const toastId = toastActions.loading({
        title: `Running ${action.display_name}...`,
        description: `Applying ${action.display_name} to ${selectedCaseIds.length} ${pluralizeWord(selectedCaseIds.length, "case")}. This may take a few seconds.`,
        duration: Infinity,
      });

      try {
        const result = await submitBulkAction({
          caseIds: selectedCaseIds,
          actionKey: action.key,
          formData,
        });

        showBulkActionResultToast(toastId, action, result, {
          onViewErrorCases: onShowErrorCases,
          onRetry: () => handleBulkActionSubmit(action, formData),
          selectedCases: bulkActionsState.selectedCases,
        });

        onSelectRow({});
      } catch (error) {
        logger.error("Error submitting bulk action", error);
        toastActions.failure({
          id: toastId,
          title: "Bulk action failed",
          description: "An unexpected error occurred. Please try again.",
          actionText: "Retry",
          onActionClick: () => handleBulkActionSubmit(action, formData),
        });
      }
    },
    [
      selectedCaseIds,
      submitBulkAction,
      onSelectRow,
      bulkActionsState.selectedCases,
      onShowErrorCases,
    ],
  );

  const handleBulkActionSelected = (action: CaseTemplateActionOutput) => {
    if (action.form?.fields && action.form.fields.length > 0) {
      open(
        {
          id: `bulk-action-${action.key}`,
          type: "cm-bulk-action",
          target: formWindowTargetRef,
          positionOptions: {
            placement: "left-start",
            middleware: [offset({ mainAxis: 10, crossAxis: 0 })],
          },
        },
        {
          actionInTemplate: action,
          selectedCasesCount: selectedCaseIds.length,
          onSubmit: handleBulkActionSubmit,
        },
      );
    } else {
      setBulkActionModalState({ isOpen: true, action });
    }
  };

  const noOfSelectedCases = selectedCaseIds.length;
  if (!noOfSelectedCases) return null;

  return (
    <div
      className="flex gap-x-2 whitespace-nowrap border-gray-200 border-l p-3.5 align-middle"
      data-loc="cases-row-selection"
    >
      <span className="font-inter-medium-13px text-gray-500">
        {pluralize(noOfSelectedCases, "case")} selected
      </span>
      <OptionsDropdown
        buttonDataLoc="cases-bulk-actions"
        elements={[
          {
            key: `Claim ${pluralizeWord(noOfSelectedCases, "case")}`,
            icon: faSuitcase,
            action: handleClaimCases,
          },
          ...(cases.canAssign
            ? [
                {
                  key: `Assign ${pluralizeWord(noOfSelectedCases, "case")}`,
                  icon: faUser,
                  action: () => setIsBulkAssignModalOpen(true),
                },
              ]
            : []),
          ...(cases.canManage && environment === "sandbox"
            ? [
                {
                  key: `Archive ${pluralizeWord(noOfSelectedCases, "case")}`,
                  icon: faArchive,
                  action: handleArchiveCases,
                },
              ]
            : []),
          ...(bulkActionsState.commonActions.length > 0 ||
          bulkActionsState.error
            ? [
                MENU_DIVIDER,
                ...bulkActionsState.commonActions.map((commonAction) => ({
                  key: commonAction.display_name,
                  action: () => handleBulkActionSelected(commonAction.action),
                })),
                ...(bulkActionsState.error
                  ? [
                      {
                        key: "No custom bulk actions available",
                        disabled: bulkActionsState.errorMessage || true,
                        action: () => {},
                        suffix: <Icon icon={faInfoCircle} size="xs" />,
                      },
                    ]
                  : []),
              ]
            : []),
        ]}
      >
        <Button iconRight={faChevronDown} size="sm" variant="secondary">
          Actions
        </Button>
      </OptionsDropdown>

      {cases.canAssign && (
        <CasesBulkAssignModal
          isAssigning={isAssigning}
          isOpen={isBulkAssignModalOpen}
          onAssign={handleAssignCases}
          onClose={() => setIsBulkAssignModalOpen(false)}
        />
      )}
      <BulkCaseActionModal
        action={bulkActionModalState.action}
        isOpen={bulkActionModalState.isOpen}
        selectedCasesCount={selectedCaseIds.length}
        onClose={() => setBulkActionModalState({ isOpen: false, action: null })}
        onSubmit={handleBulkActionSubmit}
      />

      <div ref={formWindowTargetRef} />
    </div>
  );
};

export const CasesInnerPage: React.FC = () => {
  const navigate = useNavigate();
  const { cases, caseQueues } = useCapabilities();
  const { orgId, wsId, queue_id: queueId } = useParamsDecode<CasesPageParams>();
  const [environment] = useEnvironment();
  const {
    data: queues,
    isLoading: isLoadingQueues,
    error: queuesError,
  } = useCaseQueues();
  const { refreshQueue, isRefreshingQueue } = useRefreshQueue();

  const [bulkActionErrorModal, setBulkActionErrorModal] = useState<{
    isOpen: boolean;
    cases: BulkActionErrorCaseData[];
    errorType: BulkActionErrorType;
  }>({
    isOpen: false,
    cases: [],
    errorType: BulkActionErrorType.INCOMPLETE_TASKS,
  });

  const hasQueues = !!queues?.length;
  const isAccessDenied = queuesError?.response?.status === 403;
  const currentQueue = queues?.find((q) => q.id === queueId);
  const { status: resolvedVersionStatus } =
    useResolvedTemplateVersion(currentQueue);
  const canCreateCase = resolvedVersionStatus === "ok";

  if (!isLoadingQueues && hasQueues && !queueId) {
    const defaultQueue = queues[0];

    return (
      <Navigate
        to={getUrlToCaseQueuePage({
          orgId,
          wsId,
          queueId: defaultQueue.id,
          environment,
        })}
        replace
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Cases"
        sideContent={
          <div className="h-full w-12">
            <CasesEllipsisMenu />
          </div>
        }
      />
      <CasesTableProvider>
        <SubHeader
          leftSlot={
            <>
              {isLoadingQueues && <SubHeaderLoadingTabs />}
              {!isLoadingQueues && hasQueues && (
                <SubHeaderTabs dataLoc="case-queue-tabs">
                  {queues?.map((queue) => (
                    <SubHeaderTab
                      key={String(queue.id)}
                      dataLoc={`queue-tab-${queue.id}`}
                      icon={getCaseQueueIcon(queue.icon) ?? faTable}
                      rightSlot={
                        caseQueues.canEdit ? (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <EllipsisOptionsDropdown
                              dataLoc={`queue-tab-menu-${queue.id}`}
                              elements={[
                                {
                                  key: "Edit queue",
                                  icon: faEdit,
                                  action: () =>
                                    navigate(
                                      getUrlToEditCaseQueuePage({
                                        wsId,
                                        orgId,
                                        queueId: String(queue.id),
                                      }),
                                    ),
                                },
                                {
                                  key: "Copy queue ID",
                                  icon: faCopy,
                                  action: async () => {
                                    try {
                                      await copyTextToClipboard(queue.id);
                                      toastActions.success({
                                        title: "Queue ID copied to clipboard!",
                                      });
                                    } catch {
                                      toastActions.failure({
                                        title: "Failed to copy queue ID",
                                        description: `Copy the queue ID manually: ${queue.id}`,
                                      });
                                    }
                                  },
                                },
                              ]}
                              iconSize="xs"
                              placement="bottom start"
                            />
                          </div>
                        ) : undefined
                      }
                      to={getUrlToCaseQueuePage({
                        wsId,
                        orgId,
                        queueId: String(queue.id),
                        environment,
                      })}
                    >
                      {queue.name}
                    </SubHeaderTab>
                  ))}
                </SubHeaderTabs>
              )}
              {caseQueues.canCreate && (
                <span className="flex-1">
                  <Tooltip
                    dataLoc="create-case-queue-button-tooltip"
                    delay={350}
                    title="Create queue"
                    asChild
                  >
                    <button
                      className="p-3.5 focus:outline-none focus:ring-2 focus:ring-indigo-500/25 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50"
                      data-loc="create-case-queue-button"
                      type="button"
                      onClick={() =>
                        navigate(getUrlToCreateCaseQueuePage({ orgId, wsId }))
                      }
                    >
                      <Icon icon={faPlus} size="xs" />
                    </button>
                  </Tooltip>
                </span>
              )}
            </>
          }
        >
          {hasQueues && (
            <>
              {cases.canAccess && (
                <SubHeaderRowSelection
                  wsId={wsId}
                  onShowErrorCases={(cases, errorType) =>
                    setBulkActionErrorModal({ isOpen: true, cases, errorType })
                  }
                />
              )}
              <CasesFilterPopover />
              {isFeatureFlagEnabled(FEATURE_FLAGS.manualCaseCreation) &&
                queueId && (
                  <SubHeaderButton
                    dataLoc="create-case-button"
                    disabled={!canCreateCase}
                    icon={faPlus}
                    tooltip={getCreateCaseTooltip(resolvedVersionStatus)}
                    onClick={() =>
                      navigate(
                        getUrlToCreateCasePage({
                          wsId,
                          orgId,
                          queueId,
                        }),
                      )
                    }
                  />
                )}
              <SubHeaderButton
                dataLoc="cases-refresh-button"
                disabled={isRefreshingQueue}
                icon={faRefresh}
                spin={isRefreshingQueue}
                tooltip="Refresh"
                onClick={refreshQueue}
              />
            </>
          )}
        </SubHeader>

        <div className="flex h-full min-w-[300px] flex-col gap-y-4 overflow-hidden p-5">
          {!isLoadingQueues && hasQueues && (
            <ErrorBoundary
              key={queueId}
              fallbackErrorContent={({ resetErrorBoundary }) => (
                <WhitePane>
                  <EmptyState
                    actionButton={{
                      text: "Retry",
                      icon: faRefresh,
                      onClick: resetErrorBoundary,
                    }}
                    dataLoc="cases-content-error"
                    description="We're having trouble displaying the cases. Please try again or contact support if the problem persists."
                    headline="Something went wrong"
                    icon={faExclamationCircle}
                    variant="error"
                  />
                </WhitePane>
              )}
            >
              <CaseQueueSummary />
              <CasesTable />
            </ErrorBoundary>
          )}

          {!isLoadingQueues && !hasQueues && (
            <WhitePane>
              <EmptyState
                actionButton={
                  !isAccessDenied && caseQueues.canCreate
                    ? {
                        text: "Create Queue",
                        icon: faPlus,
                        linkTo: getUrlToCreateCaseQueuePage({ orgId, wsId }),
                      }
                    : undefined
                }
                dataLoc={(() => {
                  if (isAccessDenied) {
                    return "case-queues-access-denied";
                  }
                  if (caseQueues.canCreate) {
                    return "case-queues-empty-state";
                  }
                  return "case-queues-not-assigned";
                })()}
                description={(() => {
                  if (isAccessDenied) {
                    return "You don't have permission to view case queues. Please contact your administrator.";
                  }
                  if (caseQueues.canCreate) {
                    return "There are no queues found in your workspace. Please create a queue to manage your cases.";
                  }
                  return "You have not been assigned to any queue yet. Please contact your administrator to be added to a queue.";
                })()}
                headline={(() => {
                  if (isAccessDenied) {
                    return "Access denied";
                  }
                  if (caseQueues.canCreate) {
                    return "No queues found";
                  }
                  return "No queue assignments";
                })()}
                icon={isAccessDenied ? faLock : faSearch}
                variant={isAccessDenied ? "error" : "default"}
              />
            </WhitePane>
          )}
        </div>
      </CasesTableProvider>

      <BulkActionErrorModal
        cases={bulkActionErrorModal.cases}
        errorType={bulkActionErrorModal.errorType}
        isOpen={bulkActionErrorModal.isOpen}
        orgId={orgId}
        wsId={wsId}
        onClose={() =>
          setBulkActionErrorModal({
            isOpen: false,
            cases: [],
            errorType: BulkActionErrorType.INCOMPLETE_TASKS,
          })
        }
      />
    </div>
  );
};

export const CasesPage: React.FC = () => {
  useDocumentTitle(["Cases"]);
  return (
    <FloatingWindowsProvider>
      <CasesInnerPage />
    </FloatingWindowsProvider>
  );
};
